import {
	Component,
	ChangeDetectionStrategy,
	ViewEncapsulation,
	HostBinding,
	ChangeDetectorRef
} from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';

import { Order } from '../../../order/models/order';
import { DialogResponse, OrderComment } from '../../../order/models/exception-report';

import { DialogPinComponent } from './dialog-pin/dialog-pin.component';

@Component({
	selector: 'copo-pin-orders-cell',
	templateUrl: './pin-orders-cell.component.html',
	styleUrl: './pin-orders-cell.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [NgIf, MatButtonModule, MatIconModule]
})
export class PinOrdersCellComponent implements ICellRendererAngularComp {
	@HostBinding('class.passthrough') readonly passthrough = true;
	params?: ICellRendererParams;
	orderData?: Order;
	isDisabled = false;
	private dialogSubscription: Subscription = new Subscription();

	constructor(
		readonly changeDetectorRef: ChangeDetectorRef,

		readonly dialog: MatDialog,
	) {}

	agInit(params: ICellRendererParams<Order, unknown>) {
		this.params = params;
		this.orderData = params.data;
		this.changeDetectorRef.markForCheck();
	}

	refresh(params: ICellRendererParams<Order, unknown>) {
		this.orderData = params.data;
		this.changeDetectorRef.markForCheck();
		return true;
	}

	destroy() {
		this.dialogSubscription.unsubscribe();
	}

	openPinOrder() {
		const dialogRef = this.dialog.open(DialogPinComponent, {
			width: '60%',
			data: this.orderData
		});
		this.dialogSubscription = dialogRef.afterClosed().subscribe((result: DialogResponse) => {
			if (result.action === 'submit' && this.params) {
				if (!this.orderData) {
					return;
				}
				const orderComment: OrderComment = {
					comment: result.data.comment,
					yearWeek: result.data.yearWeek
				};
				if (!result.isDeletion) {
					// Handling addition or update of comments
					this.orderData.comments ||= [];
					const existingIndex = this.orderData.comments
						.findIndex(comment => comment.yearWeek === orderComment.yearWeek);

					if (existingIndex !== -1) {
						// Update existing comment
						this.orderData.comments[existingIndex] = orderComment;
					} else {
						// Add new comment
						this.orderData.comments.push(orderComment);
					}

					this.orderData.exceptionReport = {
						commentExistForCurrentWeek: true,
						commentFromCustomer: result.data.comment,
						docNo: result.data.docNo,
						yearWeek: result.data.yearWeek
					};

					this.orderData.recentComments ||= [];
					const existingRecentIndex = this.orderData.recentComments
						.findIndex(recentComment => recentComment.yearWeek === orderComment.yearWeek);

					if (existingRecentIndex !== -1) {
						// Update recent comment
						this.orderData.recentComments[existingRecentIndex] = orderComment;
					} else {
						// Add to recent comments
						this.orderData.recentComments.push(orderComment);
					}
				} else {
					// Handling deletion of comments
					if (!this.orderData.comments || !this.orderData.recentComments) {
						return;
					}

					const existingIndex = this.orderData.comments
						.findIndex(comment => comment.yearWeek === result.data.yearWeek);
					const existingRecentIndex = this.orderData.recentComments
						.findIndex(recentComment => recentComment.yearWeek === result.data.yearWeek);

					if (existingIndex !== -1) {
						// Delete comment
						this.orderData.comments.splice(existingIndex, 1);
						this.orderData.exceptionReport = undefined;
					}

					if (existingRecentIndex !== -1) {
						// Delete recent comment
						this.orderData.recentComments.splice(existingRecentIndex, 1);
					}
				}
				this.params.node.setData(this.orderData);
			}
		});
	}
}
